/**
 * FETBetaResultSeries
 *
 * What the clinic has on file for this cycle's beta: the order's state, every
 * value it has returned so far in sequence, and the clinic's own reading of
 * them.
 *
 * It used to live inside `WaitingPhaseView`, which is no longer rendered on an
 * FET cycle — the Outcome tab keeps one design now, the step's own panel. A
 * single beta reaches that panel through the recorded result, but a series does
 * not: a rising or falling pair is the whole clinical point of repeating the
 * test, and it was only ever drawn here. So the card moved to where the patient
 * now looks instead of going away with the surface that happened to host it.
 *
 * Reads only. Every value here belongs to the clinic, and nothing on this card
 * offers to change one.
 *
 * The measurements come from the canonical cycle series, not from the FET
 * order's own result rows (#3490) — one beta-hCG contract serves every
 * modality. What stays here is the FET workflow the series deliberately does
 * not carry: the order's state, its collection window, and the clinic's
 * interpretation.
 */

import { View } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import {
  CardSubtitleText,
  CardSurface,
  CardTitleText,
  SupportingSubtitleText,
  SectionTitle,
} from "@louise/moon-ui";
import { EMBER } from "../../../../../onboarding/theme";
import { semanticColors } from "@louise/theme/tokens";
import { useTranslations, useLocale } from "@src/packages/i18n";
import type {
  FETBetaLifecycle,
  FETBetaOrder,
} from "@src/packages/cycles/useFETBetaLifecycle";
import {
  getFETBetaOrderDate,
  selectLatestFETBetaOrder,
} from "@src/packages/cycles/useFETBetaLifecycle";
import type { BetaHcgDisplayRow } from "@src/packages/cycles/presenters/betaHcgSeries";
import { BetaHcgSeriesList, formatBetaDay } from "./BetaHcgSeriesList";

const FET_BETA_STATUS_KEYS: Record<FETBetaOrder["status"], string> = {
  ordered: "care.treatment_hub.waiting_phase.beta_order_status_ordered",
  scheduled: "care.treatment_hub.waiting_phase.beta_order_status_scheduled",
  collected: "care.treatment_hub.waiting_phase.beta_order_status_collected",
  missed: "care.treatment_hub.waiting_phase.beta_order_status_missed",
  cancelled: "care.treatment_hub.waiting_phase.beta_order_status_cancelled",
  changed: "care.treatment_hub.waiting_phase.beta_order_status_changed",
  completed: "care.treatment_hub.waiting_phase.beta_order_status_completed",
  unknown: "care.treatment_hub.waiting_phase.beta_order_status_unknown",
};

type FETBetaInterpretation = FETBetaOrder["interpretations"][number];

const FET_BETA_INTERPRETATION_KEYS: Record<
  FETBetaInterpretation["state"],
  string
> = {
  negative:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_negative",
  indeterminate:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_indeterminate",
  pregnancy_detected:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_pregnancy_detected",
  pregnancy_of_unknown_location:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_unknown_location",
  intrauterine_pregnancy:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_intrauterine",
  ectopic_concern:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_ectopic_concern",
  ectopic_diagnosis:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_ectopic_diagnosis",
  biochemical_pregnancy:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_biochemical",
  early_pregnancy_loss:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_early_loss",
  ongoing_pregnancy:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_ongoing",
  live_birth:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_live_birth",
  unavailable:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_unavailable",
  unknown: "care.treatment_hub.waiting_phase.beta_interpretation_state_unknown",
  corrected:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_corrected",
  refuted: "care.treatment_hub.waiting_phase.beta_interpretation_state_refuted",
};

const FET_BETA_FOLLOW_UP_KEYS: Record<
  FETBetaInterpretation["followUpAction"],
  string
> = {
  repeat_beta_hcg: "care.treatment_hub.waiting_phase.beta_follow_up_repeat",
  ultrasound: "care.treatment_hub.waiting_phase.beta_follow_up_ultrasound",
  clinic_review: "care.treatment_hub.waiting_phase.beta_follow_up_review",
  urgent_evaluation: "care.treatment_hub.waiting_phase.beta_follow_up_urgent",
  none: "care.treatment_hub.waiting_phase.beta_follow_up_none",
  unknown: "care.treatment_hub.waiting_phase.beta_follow_up_unknown",
};

function selectCurrentFETBetaInterpretation(
  order: FETBetaOrder | null,
): FETBetaInterpretation | null {
  return (
    [...(order?.interpretations ?? [])]
      .filter(
        (candidate) =>
          candidate.state !== "corrected" && candidate.state !== "refuted",
      )
      .sort((left, right) =>
        right.interpretedAt.localeCompare(left.interpretedAt),
      )[0] ?? null
  );
}

export function FETBetaResultSeries({
  lifecycle,
  results,
}: {
  lifecycle: FETBetaLifecycle | null;
  /** The canonical cycle series — the only measurement source. */
  results: BetaHcgDisplayRow[];
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const order = selectLatestFETBetaOrder(lifecycle);
  const interpretation = selectCurrentFETBetaInterpretation(order);
  const orderDate = getFETBetaOrderDate(order);
  const status = order
    ? t(
        FET_BETA_STATUS_KEYS[order.status] ??
          "care.treatment_hub.waiting_phase.beta_order_status_unknown",
      )
    : t("care.treatment_hub.waiting_phase.beta_order_status_not_reported");

  return (
    <CardSurface testID="fet-beta-order-status" style={{ padding: 16, gap: 8 }}>
      <View>
        <CardTitleText>
          {t("care.treatment_hub.waiting_phase.beta_order_title")}
        </CardTitleText>
        <CardSubtitleText>
          {t("care.treatment_hub.waiting_phase.beta_order_status", {
            params: { status },
          })}
        </CardSubtitleText>
      </View>
      {orderDate ? (
        <SupportingSubtitleText style={{ marginTop: 0 }}>
          {t("care.treatment_hub.waiting_phase.beta_order_collection_window", {
            params: { date: formatBetaDay(orderDate, locale) },
          })}
        </SupportingSubtitleText>
      ) : null}
      {results.length > 0 ? (
        <BetaHcgSeriesList rows={results} />
      ) : (
        <SupportingSubtitleText style={{ marginTop: 0 }}>
          {t(
            order
              ? "care.treatment_hub.waiting_phase.beta_order_no_result"
              : "care.treatment_hub.waiting_phase.beta_order_no_order",
          )}
        </SupportingSubtitleText>
      )}
      {interpretation ? (
        <View
          testID="fet-beta-clinic-interpretation"
          style={{
            marginTop: 4,
            paddingTop: 10,
            borderTopWidth: 1,
            borderTopColor: semanticColors.border.subtle,
            gap: 5,
          }}
        >
          <SectionTitle level="subsection">
            {t("care.treatment_hub.waiting_phase.beta_interpretation_title")}
          </SectionTitle>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
            }}
          >
            {t("care.treatment_hub.waiting_phase.beta_interpretation_state", {
              params: {
                state: t(FET_BETA_INTERPRETATION_KEYS[interpretation.state]),
              },
            })}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
            }}
          >
            {t(
              "care.treatment_hub.waiting_phase.beta_interpretation_follow_up",
              {
                params: {
                  action: t(
                    FET_BETA_FOLLOW_UP_KEYS[interpretation.followUpAction],
                  ),
                  date: interpretation.followUpDueAt
                    ? formatBetaDay(
                        interpretation.followUpDueAt.slice(0, 10),
                        locale,
                      )
                    : t(
                        "care.treatment_hub.waiting_phase.beta_interpretation_no_due_date",
                      ),
                },
              },
            )}
          </Text>
          {interpretation.urgentEscalation ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 18,
                color: EMBER.colors.warm,
                fontWeight: "700",
              }}
            >
              {t("care.treatment_hub.waiting_phase.beta_interpretation_urgent")}
            </Text>
          ) : null}
        </View>
      ) : null}
    </CardSurface>
  );
}
